/**
 * <TitleMedal> — un titre du jeu, posé comme une médaille : la rosette, puis son nom (molecule).
 *
 * Le badge du livre (chap. 16, « Badge » : une rosette corail à étoile blanche, et son nom en gras
 * dessous). L'app n'a pas de badges ; elle a des titres, gagnés étape par étape dans une quête.
 * C'est la même chose pour l'élève : ce qu'il gagne et garde. Partout où un titre se gagne (une
 * carte de quête, une étape, l'accusé d'un achat), il se pose ainsi, jamais dans une chip au glyphe
 * de trait (décision de Wajih, 15/10).
 *
 * Deux poses : `inline` (défaut), la rosette sur sa pastille à gauche du nom, dans une rangée de
 * récompenses ; `stacked`, la rosette en grand au-dessus du nom, là où le titre est LE moment
 * (l'accusé d'un achat qui vient de le donner).
 *
 * Le ton suit la règle du livre : `gain` (corail, à gagner ou tout juste gagné), `muted` (le titre
 * d'une étape pas encore débloquée). Le badge du livre est corail même gagné : c'est la coche ou la
 * barre à côté qui dit l'acquis en vert.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"
import { GAME_GLYPH_SIZE, GameGlyph, type GameGlyphTone } from "../GameGlyph"

export type TitleMedalProps = {
    /** Le titre : « Ninja », « Étoile naissante ». */
    title: string
    /** `inline` (défaut) ou `stacked`. */
    layout?: "inline" | "stacked"
    /** `gain` (défaut) ou `muted`. */
    tone?: Extract<GameGlyphTone, "gain" | "muted">
    testID?: string
}

export function TitleMedal(props: TitleMedalProps) {
    const { title, layout = "inline", tone = "gain", testID } = props
    const muted = tone === "muted"

    if (layout === "stacked") {
        return (
            <View testID={testID} accessible accessibilityLabel={`Titre : ${title}`} style={styles.stacked}>
                <GameGlyph name="title" size={GAME_GLYPH_SIZE.hero} tone={tone} plate={false} />
                <Text size="text-xl" weight="bold" center>
                    {title}
                </Text>
            </View>
        )
    }

    return (
        <View testID={testID} accessible accessibilityLabel={`Titre : ${title}`} style={styles.inline}>
            <Row gap="2">
                {/* À venir, sans pastille : sept étapes de pastilles grises pèseraient plus que l'étape en cours. */}
                {muted ? (
                    <GameGlyph name="title" size={GAME_GLYPH_SIZE.chip + 4} tone={tone} plate={false} />
                ) : (
                    <GameGlyph name="title" size={GAME_GLYPH_SIZE.row} tone={tone} />
                )}
                <Text
                    size="text-sm"
                    weight={muted ? "regular" : "bold"}
                    color={muted ? "text-muted" : "text-primary"}
                    numberOfLines={1}
                    style={styles.name}
                >
                    {title}
                </Text>
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    inline: { flexShrink: 1, minWidth: 0 },
    name: { flexShrink: 1 },
    stacked: { alignItems: "center", gap: theme.spacing["2"] },
}))
